import React, { useState, useEffect, useRef, use } from "react";
import styles from "./VesselTracker.module.css";
import { useVesselContext } from "../../../contexts/VesselContext";
import { useSidebarContext } from "../../../contexts/SidebarContext";
import { useVesselPositions } from "../../../hooks/useVesselPossitions";
import { positionTypeDescriptions, positionIcon } from "../../../constants/Position";
import { toDMSWithDirection } from "../../../utils/geo";
import { useAppContext } from "../../../contexts/AppContext";
import Btn from "../../Btn/Btn";
import L, { map } from "leaflet";
import { useMapDisplayContext } from "../../../contexts/MapDisplayContext";
import { mapBounds, rankZoom } from "../../../constants/Map";
import dayjs from "dayjs";
// Helper to format date to yyyy-mm-dd
function formatDateInput(date: Date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

const VesselTracker: React.FC = () => {
    const { vessels, currentPositionDataRange, setCurrentPositionDataRange, vesselsPositionDataTimeRange } = useVesselContext();
    const { vesselBeingTrackedId, chosenPosition } = useSidebarContext();
    const { getPositionsQueued } = useVesselPositions();
    const { setLoading, showDialog } = useAppContext();
    const { action, zoomLevel } = useMapDisplayContext();
    const [track, setTrack] = useState<any[]>([]);
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const [openIndex, setOpenIndex] = useState<number | null>(null);
    const zoomLevelRef = useRef<number>(zoomLevel);
    const vesselBeingTrackedIdRef = useRef<string | null>(vesselBeingTrackedId);
    useEffect(() => {
        vesselBeingTrackedIdRef.current = vesselBeingTrackedId;
    }, [vesselBeingTrackedId]);
    useEffect(() => {
        zoomLevelRef.current = zoomLevel;
    }, [zoomLevel]);
    useEffect(() => {
        if (!vesselBeingTrackedId || !vessels[vesselBeingTrackedId]) return;
        const positions = vessels[vesselBeingTrackedId].positions ?? [];
        setTrack(positions.filter(
            (x: any) => x.arrivedtime >= currentPositionDataRange[0] && x.arrivedtime <= currentPositionDataRange[1]
        ));
    }, [vesselBeingTrackedId, vessels, currentPositionDataRange]);

    useEffect(() => {
        // Set initial date values
        const from = new Date(currentPositionDataRange[0]);
        const to = new Date(currentPositionDataRange[1]);
        setFromDate(formatDateInput(from));
        setToDate(formatDateInput(to));
    }, [currentPositionDataRange]);

    const handleDateChange = (type: "from" | "to", value: string) => {
        if (type == "from") {
            setFromDate(value);
            if (toDate && value > toDate) setToDate(value);
        } else {
            setToDate(value);
            if (fromDate && value < fromDate) setFromDate(value);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!fromDate || !toDate) return;
        const from = dayjs(fromDate).startOf("day").toDate();
        const to = dayjs(toDate).endOf("day").toDate();
        const range: [number, number] = [from.getTime(), Math.min(to.getTime(), Date.now())];
        if(range[0] >= range[1]){
            showDialog("Lỗi", "Khoảng thời gian không hợp lệ!", "error");
            return;
        }
        const lastTimeRange = vesselsPositionDataTimeRange.at(-1);
        range[1] = Math.min(range[1], lastTimeRange ? lastTimeRange[1] : Date.now());
        setCurrentPositionDataRange(range);
        setLoading(true);
        await getPositionsQueued(range[0], range[1]);
        setLoading(false);
        action.clickVesselMarker(vesselBeingTrackedIdRef.current!);
    };
    const trackReverse = track.slice().reverse();
    const handleItemClick = (i: number) => {
        setOpenIndex(prev => (prev == i ? null : i));
        if(mapBounds.contains(L.latLng(trackReverse[i].latitude, trackReverse[i].longitude))) {
            action.setView(trackReverse[i].latitude, trackReverse[i].longitude,
                zoomLevelRef.current > rankZoom["track-point"].minZoom ? zoomLevelRef.current : rankZoom["track-point"].minZoom + 1
            );
        }
    };

    const handleItemMouseOver = (i: number) => {
        action.highlightPosition(L.latLng(trackReverse[i].latitude, trackReverse[i].longitude));
    };
    const handleItemMouseOut = () => {
        action.highlightPosition(L.latLng(0, 0));
    };
    // theo dõi vị trí được highlight
    useEffect(() => {
        if (chosenPosition) {
            trackReverse.forEach((item, i) => {
                if (item.latitude == chosenPosition.lat && item.longitude == chosenPosition.lng) {
                    setOpenIndex(prev => (prev == i ? null : i));
                }
            });
        }
    }, [chosenPosition]);
    //scroll đến item được mở
    useEffect(() => {
        if (openIndex != null) {
            const element = document.querySelector(`[data-index="${openIndex}"]`);
            if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
            }
        }
    }, [openIndex]);

    if (!vesselBeingTrackedId || !vessels[vesselBeingTrackedId]) {
        return (
            <div className={styles["ship-header"] + " text"}>
                Hãy chọn 1 tàu để hiển thị
            </div>
        );
    }
    const vessel = vessels[vesselBeingTrackedId];

    return (
        <>
            <div className={`${styles["ship-header"]} text`}>
                Tàu {vessel.vesselName || vesselBeingTrackedId}
            </div>
            <div className={`${styles["history-date"]} text`}>
                <form id="rangeForm" noValidate className="needs-validation text row" onSubmit={handleSubmit}>
                    <div className="row col-md-10">
                        <div className="col-12 mb-1">
                            <div className="input-group">
                                <span className="text-dark input-group-text">Từ</span>
                                <input
                                    type="date"
                                    id="fromDate"
                                    className="text-dark form-control rounded-end track-mode-date-picker"
                                    required
                                    value={fromDate}
                                    max={toDate}
                                    onChange={e => handleDateChange("from", e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-12 mb-1">
                            <div className="input-group">
                                <span className="text-dark input-group-text">Đến</span>
                                <input
                                    type="date"
                                    id="toDate"
                                    className="text-dark form-control rounded-end track-mode-date-picker"
                                    required
                                    value={toDate}
                                    min={fromDate}
                                    max={formatDateInput(new Date())}
                                    onChange={e => handleDateChange("to", e.target.value)}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-2 d-flex justify-content-center align-items-center gap-2">
                        <Btn className="text-small" type="submit">Áp dụng</Btn>
                    </div>
                </form>
            </div>
            <div className={`${styles["history-list"]} styled-scrollbar`}>
                {trackReverse.map((item, i) => (
                    <div
                        className={`${styles["history-item"]}`}
                        key={i}
                        data-index={i}
                        id={vesselBeingTrackedId}
                        onClick={() => handleItemClick(i)}
                        onMouseOver={() => handleItemMouseOver(i)}
                        onMouseOut={() => handleItemMouseOut()}
                    >
                        <div
                            className={styles["history-info"]}
                            position-type={item.type ?? "POS"}
                        >
                            <div className="time">{new Date(item.positiontime * 1000).toLocaleString("vi-VN")}</div>
                            <div className="location">
                                Vị trí: {toDMSWithDirection(item.latitude, true)}, {toDMSWithDirection(item.longitude, false)}
                            </div>
                        </div>
                        <div className={`text-small ${openIndex == i ? styles["extra-info-open"] : styles["extra-info"]}`}>
                            <hr />
                            <div>Tốc độ: {item.sog ?? "N/A"} km/h</div>
                            <div>Hướng: {item.cog ?? "N/A"}°</div>
                            <div className="text-small">
                                Loại: {positionTypeDescriptions[(item.type as keyof typeof positionTypeDescriptions) ?? "POS"]}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
};

export default VesselTracker;
